import { autocompleteClasses, Box, Button, Paper, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { ControlledAutocomplete, ControlledTextField, Form, useHookForm } from "@powerdiary/components-library/lib";
import Content from "../Layout/Content";
import {
    buildWaitlistRequestSchema,
    getOptionDisabledForDropdown,
    waitlistRequestFormDefaultValues,
    WaitlistRequestFormSchemaType,
} from "./WaitlistRequest.helpers";
import AvailableTimePicker from "./AvailableTimePicker";
import { useAuthorizationContext, useClientPortalConfigContext } from "../../context";
import { useWaitlistRequestConfiguration } from "./hooks/useWaitlistRequestConfiguration";
import LoadingOverlay from "../Shared/LoadingOverlay";
import { useEffect } from "react";
import { useSubmitWaitlistRequest } from "./hooks/useSubmitWaitlistRequest";
import { useNavigate, useSearchParams } from "react-router";
import { ROUTES } from "../../routes";
import { useWaitlistRequestData } from "./hooks/useWaitlistRequestData";
import { SelectListItem, WaitlistRequestSubmitModel } from "../../types/generated/models";
import { useClientPortalHomeActions } from "../Home/hooks/useClientPortalHomeActions";
import Login from "../Account/LoginDialog";
import { useAnalytics } from "../../hooks/useAnalytics";

export default function WaitlistRequest() {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
    const { clientPortalGuid, clientPortalConfig } = useClientPortalConfigContext();
    const { data, isLoading, refetchWaitlistRequestConfiguration } = useWaitlistRequestConfiguration(clientPortalGuid);
    const { submitWaitlistRequest, isPending } = useSubmitWaitlistRequest();
    const { practitionerOptions, locationOptions } = useWaitlistRequestData();
    const { setShowLoginDialog } = useClientPortalHomeActions();
    const { loggedIn } = useAuthorizationContext();
    const [searchParams] = useSearchParams();
    const { track } = useAnalytics();

    useEffect(() => {
        track("page_view", { page_title: "Waitlist Request" });
    }, [track]);

    const form = useHookForm({
        defaultValues: waitlistRequestFormDefaultValues,
        schema: clientPortalConfig
            ? buildWaitlistRequestSchema(clientPortalConfig.clientsCanBookWithAnyPractitioner)
            : undefined,
    });

    const { setValue, watch, getValues } = form;

    useEffect(() => {
        const setOption = (paramKey: string, options: SelectListItem[], fieldName: "practitioners" | "locations") => {
            const paramValue = searchParams.get(paramKey);

            if (!paramValue) return;

            const selected = options.find((o) => o.value === paramValue);
            if (selected) {
                setValue(fieldName, [selected]);
            }
        };

        setOption("practitionerId", practitionerOptions, "practitioners");
        setOption("locationId", locationOptions, "locations");
    }, [searchParams, practitionerOptions, locationOptions, setValue]);

    useEffect(() => {
        if (loggedIn) {
            const fetchData = async () => {
                await refetchWaitlistRequestConfiguration();
            };
            void fetchData();
        }
    }, [loggedIn, refetchWaitlistRequestConfiguration]);

    const availableTimes = watch("availableTimes");

    useEffect(() => {
        if (!(clientPortalConfig.featureAccess?.canRequestWaitlist ?? false)) {
            void navigate(`/clientportal/${clientPortalGuid}/${ROUTES.NOT_AVAILABLE.urlPath}`);
        }
    }, [navigate, clientPortalConfig, clientPortalGuid]);

    const handleAvailableTimesChange = (value: number) => {
        setValue("availableTimes", value);
    };

    useEffect(() => {
        if (!data) return;

        const { clients } = data;

        if (clients?.length > 0) {
            setValue("waitlistFor", clients[0]);
        }
    }, [data, setValue]);

    const handleSubmit = async (formData: WaitlistRequestFormSchemaType) => {
        const { locations, practitioners } = formData;

        const locationIds = locations.some((x) => x.value === "any") ? [] : locations.map((x) => Number(x.value));
        const practitionerIds = practitioners.some((x) => x.value === "any")
            ? []
            : practitioners.map((x) => Number(x.value));

        const data = {
            availableTimes: formData.availableTimes,
            notes: formData.notes,
            waitlistFor: Number(formData.waitlistFor.value),
            practitionerIds: practitionerIds,
            locationIds: locationIds,
        } as WaitlistRequestSubmitModel;

        await submitWaitlistRequest(data, () => {
            void navigate(`/clientportal/${clientPortalGuid}/${ROUTES.HOME.urlPath}`);
        });
    };

    return isLoading ? (
        <LoadingOverlay />
    ) : (
        <Content dataTestIdSuffix="waitlist_request">
            <Stack spacing={2} p={isMobile ? 2 : 4}>
                <Typography variant="h6" fontWeight="bold">
                    Request to Join the Waitlist
                </Typography>

                <Paper>
                    <Form form={form} onSubmit={handleSubmit}>
                        <Box display="flex" flexDirection="column" alignItems="center">
                            <Stack
                                spacing={4}
                                p={isMobile ? 2 : 5}
                                sx={{
                                    maxWidth: isMobile ? "auto" : "540px",
                                }}
                            >
                                <Typography variant="heading4" fontSize={17}>
                                    Request a place on our waitlist and we&apos;ll reach out when there&apos;s
                                    availability
                                </Typography>

                                <Stack spacing={2}>
                                    {data?.showClientsSelect && (
                                        <ControlledAutocomplete
                                            label="Who do you want to place on the waitlist?"
                                            name="waitlistFor"
                                            options={data?.clients}
                                            fullWidth
                                            size="medium"
                                            isOptionEqualToValue={(option, selected) => option.value === selected.value}
                                            dataTestIdSuffix="clients"
                                        />
                                    )}
                                    <ControlledAutocomplete
                                        multiple
                                        label="Who do you want to book with?"
                                        name="practitioners"
                                        limitTags={5}
                                        size="medium"
                                        options={practitionerOptions}
                                        isOptionEqualToValue={(option, selected) => option.value === selected.value}
                                        fullWidth
                                        getOptionDisabled={(option) =>
                                            getOptionDisabledForDropdown(option, getValues("practitioners"))
                                        }
                                        dataTestIdSuffix="practitioners"
                                        sx={{
                                            [`& .${autocompleteClasses.tag}`]: {
                                                maxWidth: "125px",
                                                marginTop: "5px",
                                            },
                                        }}
                                    />

                                    <ControlledAutocomplete
                                        multiple
                                        label="At which location?"
                                        name="locations"
                                        limitTags={5}
                                        size="medium"
                                        options={locationOptions}
                                        isOptionEqualToValue={(option, selected) => option.value === selected.value}
                                        fullWidth
                                        getOptionDisabled={(option) =>
                                            getOptionDisabledForDropdown(option, getValues("locations"))
                                        }
                                        dataTestIdSuffix="locations"
                                        sx={{
                                            [`& .${autocompleteClasses.tag}`]: {
                                                maxWidth: "125px",
                                                marginTop: "5px",
                                            },
                                        }}
                                    />

                                    <Stack spacing={1}>
                                        <Typography variant="paragraphMediumBold" color="text.primary">
                                            Preferred Times
                                        </Typography>
                                        <AvailableTimePicker
                                            generalValue={availableTimes}
                                            onChange={handleAvailableTimesChange}
                                        />
                                    </Stack>

                                    <ControlledTextField
                                        label="Notes"
                                        placeholder="Tell us more about the nature of the appointment you want to schedule."
                                        name="notes"
                                        type="text"
                                        multiline
                                        rows={3}
                                        fullWidth
                                        dataTestIdSuffix="notes"
                                    />

                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={isPending}
                                        sx={{
                                            alignSelf: "flex-end",
                                        }}
                                        fullWidth={isMobile}
                                        data-test-id="submit_button"
                                    >
                                        <Typography variant="paragraphMediumBold">Submit</Typography>
                                    </Button>
                                </Stack>
                            </Stack>
                        </Box>
                    </Form>
                </Paper>
            </Stack>

            {!loggedIn && (
                <Login returnUrl={ROUTES.WAITLIST_REQUEST.urlPath} handleClose={() => setShowLoginDialog(false)} />
            )}
        </Content>
    );
}
